import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { firestore } from '../../../lib/firebase/client';

export type RegistrationMode = 'open' | 'invite-only' | 'approval';

export interface RegistrationSettings {
  mode: RegistrationMode;
  requireInviteCode: boolean;
  requireAdminApproval: boolean;
  enableCaptcha: boolean;
  captchaThreshold: number;
  updatedAt?: Date;
  updatedBy?: string;
}

export interface CaptchaSettings {
  reCaptchaV3SiteKey: string;
  reCaptchaV2SiteKey: string;
  fallbackToV2: boolean;
  enabled: boolean;
}

interface RegistrationSettingsContextValue {
  settings: RegistrationSettings | null;
  captchaSettings: CaptchaSettings | null;
  loading: boolean;
  error: Error | null;
}

export const RegistrationSettingsContext = createContext<
  RegistrationSettingsContextValue | undefined
>(undefined);

const defaultSettings: RegistrationSettings = {
  mode: 'open',
  requireInviteCode: false,
  requireAdminApproval: false,
  enableCaptcha: true,
  captchaThreshold: 0.5,
};

const defaultCaptchaSettings: CaptchaSettings = {
  reCaptchaV3SiteKey: import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY || '',
  reCaptchaV2SiteKey: import.meta.env.VITE_RECAPTCHA_V2_SITE_KEY || '',
  fallbackToV2: true,
  enabled: true,
};

export const RegistrationSettingsProvider = ({ children }: { children: ReactNode }) => {
  const [settings, setSettings] = useState<RegistrationSettings | null>(defaultSettings);
  const [captchaSettings] = useState<CaptchaSettings | null>(defaultCaptchaSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    try {
      const settingsRef = doc(firestore, 'platformSettings/registration');

      const unsubscribe = onSnapshot(
        settingsRef,
        snapshot => {
          if (snapshot.exists()) {
            const data = snapshot.data();
            setSettings({
              mode: (data.mode as RegistrationMode) || 'open',
              requireInviteCode: data.requireInviteCode || false,
              requireAdminApproval: data.requireAdminApproval || false,
              enableCaptcha: data.enableCaptcha !== false, // Default to true
              captchaThreshold: data.captchaThreshold || 0.5,
              updatedAt: data.updatedAt?.toDate(),
              updatedBy: data.updatedBy,
            });
          } else {
            // No settings document exists, use defaults
            setSettings(defaultSettings);
          }
          setLoading(false);
          setError(null);
        },
        err => {
          console.error('Error loading registration settings:', err);
          setError(err as Error);
          setSettings(defaultSettings); // Fall back to defaults on error
          setLoading(false);
        },
      );

      return unsubscribe;
    } catch (err) {
      console.error('Error setting up registration settings listener:', err);
      setError(err as Error);
      setSettings(defaultSettings);
      setLoading(false);
      return () => {};
    }
  }, []);

  return (
    <RegistrationSettingsContext.Provider
      value={{ settings, captchaSettings, loading, error }}
    >
      {children}
    </RegistrationSettingsContext.Provider>
  );
};

export const useRegistrationSettings = () => {
  const context = useContext(RegistrationSettingsContext);
  if (context === undefined) {
    throw new Error(
      'useRegistrationSettings must be used within a RegistrationSettingsProvider',
    );
  }
  return context;
};
